import React, { useState, useRef, useEffect } from "react";
import { Button, Toggle, Dropdown } from "../../../components/tailwind";
import { Icon } from "../../../components/tailwind/icon/Icon";
import type { Option } from "../../../components/tailwind/dropdown/Dropdown";
import { uuidv4 } from "../../../utils/uuidUtils";
import { ManageEnrichPromptModelSettings } from "./ManageEnrichPromptModelSettings";
import {
  GptModel,
  GptProviders,
  PromptConfigOverwrite,
} from "../../../products/copy-assistant/types";

type AdditionalInstruction = {
  id: string;
  value: string;
};

type ListRowProps = {
  instruction: AdditionalInstruction;
  onChange: (id: string, value: string) => void;
  onRemove: (id: string) => void;
  canRemove: boolean;
  inputType?: "textarea" | "input";
  placeholder?: string;
};

const ListRow: React.FC<ListRowProps> = ({
  instruction,
  onChange,
  onRemove,
  canRemove,
  inputType = "textarea",
  placeholder = "",
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (inputType === "textarea") {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }
  }, [instruction.value, inputType]);

  return (
    <div className="tw-flex tw-items-start tw-gap-2 tw-w-full">
      <div className="tw-flex-1 tw-min-w-0">
        {inputType === "input" ? (
          <input
            type="text"
            value={instruction.value}
            onChange={(e): void => {
              onChange(instruction.id, e.target.value);
            }}
            className="tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-px-2.5 tw-py-1.5"
            placeholder={placeholder}
          />
        ) : (
          <textarea
            ref={textareaRef}
            value={instruction.value}
            onChange={(e): void => {
              onChange(instruction.id, e.target.value);
              // Auto-resize on change
              e.target.style.height = "auto";
              e.target.style.height = `${e.target.scrollHeight}px`;
            }}
            rows={1}
            className="tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-px-2.5 tw-py-1.5 tw-resize-none tw-overflow-hidden"
            style={{
              minHeight: "38px",
              height: "38px",
            }}
            placeholder={placeholder}
          />
        )}
      </div>
      {canRemove && (
        <Button
          type="button"
          variant="secondary-alt"
          icon
          onClick={(): void => onRemove(instruction.id)}
          borderless
          data-balloon="Remove instruction"
          data-balloon-pos="left"
        >
          <Icon className="tw-text-red-600" name="delete" />
        </Button>
      )}
    </div>
  );
};

type Props = {
  token: string;
  globalDefaultInstruction: string;
  globalDefaultModelDisplayName: string;
  hasCustomInstruction: string;
  instruction: string;
  additionalInstructions: string; // JSON string
  modelName: string;
  configOverwrite: string; // JSON string
  useImages: string;
  imageDetail: string;
  imageDetailOptions: string; // JSON string
  modelOptions: string; // JSON string
  webIdentificationFields: string; // JSON string
  webSearchDomains: string; // JSON string
  webSearchDomainMode: string;
  webSearchEngine: string;
  webSearchEngineOptions: string; // JSON string
  webSearchContextSizeOptions: string; // JSON string
  webSearchDomainModeOptions: string; // JSON string
  webSearchPrompt: string;
  webSearchMaxResults: string;
  webSearchMaxResultsLimit: string;
  webSearchContextSize: string;
  enrichExaEnabled: string;
  exaSearchConfig: string; // JSON string
  exaSearchConfigDefault: string; // JSON string
  exaMaxTurns: string;
  supplierTextFieldId: string;
  pimDatestampFieldId: string;
  hasInriverIntegration: string;
  pimWebhookUrl: string;
  imageFilterField: string;
  imageFilterValue: string;
  formAction: string;
  csrfToken: string;
  enrichReportDownloadUrl?: string;
};

export const ManageEnrichPrompt: React.FC<Props> = ({
  globalDefaultInstruction,
  globalDefaultModelDisplayName,
  hasCustomInstruction,
  instruction: initialInstruction,
  additionalInstructions: initialAdditionalInstructionsJson,
  modelName: initialModelName,
  configOverwrite: initialConfigOverwrite,
  useImages: initialUseImages,
  imageDetail: initialImageDetail,
  imageDetailOptions: imageDetailOptionsJson,
  modelOptions: modelOptionsJson,
  webIdentificationFields: initialWebIdentificationFieldsJson,
  webSearchDomains: initialWebSearchDomainsJson,
  webSearchDomainMode: initialWebSearchDomainMode,
  webSearchEngine: initialWebSearchEngine,
  webSearchEngineOptions: webSearchEngineOptions,
  webSearchContextSizeOptions: webSearchContextSizeOptions,
  webSearchDomainModeOptions: webSearchDomainModeOptions,
  webSearchPrompt: initialWebSearchPrompt,
  webSearchMaxResults: initialWebSearchMaxResults,
  webSearchMaxResultsLimit: initialWebSearchMaxResultsLimit,
  webSearchContextSize: initialWebSearchContextSize,
  enrichExaEnabled,
  exaSearchConfig: initialExaSearchConfig,
  exaSearchConfigDefault: initialExaSearchConfigDefault,
  exaMaxTurns: initialExaMaxTurns,
  supplierTextFieldId: initialSupplierTextFieldId,
  pimDatestampFieldId: initialPimDatestampFieldId,
  hasInriverIntegration,
  pimWebhookUrl: initialPimWebhookUrl,
  imageFilterField: initialImageFilterField,
  imageFilterValue: initialImageFilterValue,
  formAction,
  csrfToken,
  enrichReportDownloadUrl,
}) => {
  const [instruction, setInstruction] = useState<string>(
    initialInstruction || ""
  );
  const [hasCustomInstructionState, setHasCustomInstructionState] = useState<
    boolean
  >(hasCustomInstruction === "true");
  const [additionalInstructions, setAdditionalInstructions] = useState<
    AdditionalInstruction[]
  >(() => {
    try {
      const parsed = JSON.parse(initialAdditionalInstructionsJson || "[]");
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((value: string) => ({
          id: uuidv4(),
          value: value,
        }));
      }
      return [{ id: uuidv4(), value: "" }];
    } catch {
      return [{ id: uuidv4(), value: "" }];
    }
  });

  const modelOptions = JSON.parse(modelOptionsJson || "[]");
  const engineOptions = JSON.parse(webSearchEngineOptions || "[]");
  const contextSizeOptions = JSON.parse(webSearchContextSizeOptions || "[]");
  const domainModeOptions = JSON.parse(webSearchDomainModeOptions || "[]");
  const imageDetailOptions = JSON.parse(imageDetailOptionsJson || "[]");
  const [selectedModel, setSelectedModel] = useState<GptModel>(
    modelOptions.find((model: GptModel) => model.model_name == initialModelName)
  );
  const [configOverwrite, setConfigOverwrite] = useState<PromptConfigOverwrite>(
    ((): PromptConfigOverwrite => {
      try {
        return JSON.parse(initialConfigOverwrite || "{}");
      } catch {
        return {};
      }
    })()
  );
  const [useImages, setUseImages] = useState<boolean>(
    initialUseImages === "true"
  );
  const [imageDetail, setImageDetail] = useState<string>(
    initialImageDetail || "low"
  );
  const [webIdentificationFields, setWebIdentificationFields] = useState<
    AdditionalInstruction[]
  >(() => {
    try {
      const parsed = JSON.parse(initialWebIdentificationFieldsJson || "[]");
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((value: string) => ({
          id: uuidv4(),
          value: value,
        }));
      }
      return [{ id: uuidv4(), value: "" }];
    } catch {
      return [{ id: uuidv4(), value: "" }];
    }
  });
  const [webSearchDomains, setWebSearchDomains] = useState<
    AdditionalInstruction[]
  >(() => {
    try {
      const parsed = JSON.parse(initialWebSearchDomainsJson || "[]");
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((value: string) => ({
          id: uuidv4(),
          value: value,
        }));
      }
      return [{ id: uuidv4(), value: "" }];
    } catch {
      return [{ id: uuidv4(), value: "" }];
    }
  });
  const [webSearchDomainMode, setWebSearchDomainMode] = useState<string>(
    initialWebSearchDomainMode === "include" ? "include" : "exclude"
  );
  const [webSearchEngine, setWebSearchEngine] = useState<string>(
    initialWebSearchEngine
  );
  const [webSearchPrompt, setWebSearchPrompt] = useState<string>(
    initialWebSearchPrompt
  );
  const webSearchMaxResultsCeiling =
    parseInt(initialWebSearchMaxResultsLimit, 10) || 1;
  const [webSearchMaxResults, setWebSearchMaxResults] = useState<number>(
    parseInt(initialWebSearchMaxResults, 10) || 1
  );
  const [webSearchContextSize, setWebSearchContextSize] = useState<string>(
    initialWebSearchContextSize
  );
  const isOpenAiModelSelected =
    selectedModel?.provider === GptProviders.OPENAI ||
    selectedModel?.model_name?.toLowerCase().startsWith("openai/") === true;
  const [supplierTextFieldId, setSupplierTextFieldId] = useState<string>(
    initialSupplierTextFieldId || ""
  );
  const [pimDatestampFieldId, setPimDatestampFieldId] = useState<string>(
    initialPimDatestampFieldId || ""
  );
  const [pimWebhookUrl, setPimWebhookUrl] = useState<string>(
    initialPimWebhookUrl || ""
  );
  const [imageFilterField, setImageFilterField] = useState<string>(
    initialImageFilterField || ""
  );
  const [imageFilterValue, setImageFilterValue] = useState<string>(
    initialImageFilterValue || ""
  );

  const isExaWebSearch = enrichExaEnabled === "true";

  // Pretty-print a JSON string for display in the Exa settings textarea.
  // Returns "" for empty/invalid input so the placeholder (global default) shows.
  const formatJson = (raw: string): string => {
    try {
      const parsed = JSON.parse(raw || "{}");
      if (
        parsed &&
        typeof parsed === "object" &&
        Object.keys(parsed).length > 0
      ) {
        return JSON.stringify(parsed, null, 2);
      }
      return "";
    } catch {
      return raw;
    }
  };

  const [exaSearchConfigText, setExaSearchConfigText] = useState<string>(() =>
    formatJson(initialExaSearchConfig)
  );
  const [exaMaxTurns, setExaMaxTurns] = useState<number>(
    parseInt(initialExaMaxTurns, 10) || 2
  );
  const exaConfigError: string | null = ((): string | null => {
    if (exaSearchConfigText.trim() === "") {
      return null;
    }
    try {
      const parsed = JSON.parse(exaSearchConfigText);
      if (
        parsed === null ||
        typeof parsed !== "object" ||
        Array.isArray(parsed)
      ) {
        return "Must be a JSON object.";
      }
      return null;
    } catch {
      return "Invalid JSON.";
    }
  })();
  const exaConfigDefaultPlaceholder = formatJson(initialExaSearchConfigDefault);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const formRef = useRef<HTMLFormElement>(null);

  // Parse model options
  const modelDropdownOptions: Option<string>[] = React.useMemo(() => {
    try {
      const parsed = JSON.parse(modelOptionsJson || "[]");
      return Array.isArray(parsed)
        ? parsed.map(
            (
              opt: {
                model_name: string;
                display_name: string;
              },
              index: number
            ) => ({
              key: `model-${index}`,
              value: opt.model_name,
              text: opt.display_name,
            })
          )
        : [];
    } catch {
      return [];
    }
  }, [modelOptionsJson]);

  const isExaEngine = webSearchEngine === "exa";

  useEffect(() => {
    if (isOpenAiModelSelected && webSearchDomainMode !== "include") {
      setWebSearchDomainMode("include");
    }
  }, [isOpenAiModelSelected, webSearchDomainMode]);

  const handleRevertInstruction = (): void => {
    setInstruction("");
    setHasCustomInstructionState(false);
  };

  const handleAddAdditionalInstruction = (): void => {
    setAdditionalInstructions([
      ...additionalInstructions,
      { id: uuidv4(), value: "" },
    ]);
  };

  const handleRemoveAdditionalInstruction = (id: string): void => {
    if (additionalInstructions.length > 1) {
      const newInstructions = additionalInstructions.filter(
        (inst) => inst.id !== id
      );
      setAdditionalInstructions(newInstructions);
    } else {
      // Keep at least one empty field
      setAdditionalInstructions([{ id: uuidv4(), value: "" }]);
    }
  };

  const handleAdditionalInstructionChange = (
    id: string,
    value: string
  ): void => {
    setAdditionalInstructions(
      additionalInstructions.map((inst) =>
        inst.id === id ? { ...inst, value } : inst
      )
    );
  };

  const handleAddWebIdentificationField = (): void => {
    setWebIdentificationFields([
      ...webIdentificationFields,
      { id: uuidv4(), value: "" },
    ]);
  };

  const handleRemoveWebIdentificationField = (id: string): void => {
    if (webIdentificationFields.length > 1) {
      const newFields = webIdentificationFields.filter(
        (field) => field.id !== id
      );
      setWebIdentificationFields(newFields);
    } else {
      // Keep at least one empty field
      setWebIdentificationFields([{ id: uuidv4(), value: "" }]);
    }
  };

  const handleWebIdentificationFieldChange = (
    id: string,
    value: string
  ): void => {
    setWebIdentificationFields(
      webIdentificationFields.map((field) =>
        field.id === id ? { ...field, value } : field
      )
    );
  };

  const handleAddWebSearchDomain = (): void => {
    setWebSearchDomains([...webSearchDomains, { id: uuidv4(), value: "" }]);
  };

  const handleRemoveWebSearchDomain = (id: string): void => {
    if (webSearchDomains.length > 1) {
      setWebSearchDomains(webSearchDomains.filter((row) => row.id !== id));
    } else {
      setWebSearchDomains([{ id: uuidv4(), value: "" }]);
    }
  };

  const handleWebSearchDomainChange = (id: string, value: string): void => {
    setWebSearchDomains(
      webSearchDomains.map((row) => (row.id === id ? { ...row, value } : row))
    );
  };

  const collectConfig = (newConfigOverwrite: PromptConfigOverwrite): void => {
    setConfigOverwrite(newConfigOverwrite);
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();

    if (isExaWebSearch && exaConfigError) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert additional instructions to JSON array
      const additionalInstructionsValue = JSON.stringify(
        additionalInstructions
          .map((inst) => inst.value)
          .filter((value) => value.trim() !== "")
      );

      // Convert web identification fields to JSON array
      const webIdentificationFieldsValue = JSON.stringify(
        webIdentificationFields
          .map((field) => field.value)
          .filter((value) => value.trim() !== "")
      );

      const webSearchDomainsValue = JSON.stringify(
        webSearchDomains
          .map((row) => row.value)
          .filter((value) => value.trim() !== "")
      );

      // Create form data
      const formData = new FormData();
      formData.append("csrfmiddlewaretoken", csrfToken);

      // Handle instruction:
      // - If hasCustomInstructionState is false, send empty string to set instruction to None (use global default)
      // - If hasCustomInstructionState is true, send the instruction value (even if it matches default)
      if (!hasCustomInstructionState) {
        formData.append("instruction", "");
      } else {
        formData.append("instruction", instruction);
      }

      formData.append("additional_instructions", additionalInstructionsValue);
      formData.append("model_name", selectedModel?.model_name ?? "");
      formData.append("use_images", useImages ? "on" : "");
      formData.append("image_detail", imageDetail);
      formData.append(
        "web_identification_fields",
        webIdentificationFieldsValue
      );
      formData.append("web_search_domains", webSearchDomainsValue);
      const webSearchDomainModeValue = isOpenAiModelSelected
        ? "include"
        : webSearchDomainMode;
      formData.append("web_search_domain_mode", webSearchDomainModeValue);
      formData.append("web_search_engine", webSearchEngine);
      formData.append("web_search_prompt", webSearchPrompt);
      formData.append("web_search_max_results", String(webSearchMaxResults));
      formData.append("web_search_context_size", webSearchContextSize);
      formData.append("supplier_text_field_id", supplierTextFieldId);
      formData.append("pim_datestamp_field_id", pimDatestampFieldId);
      formData.append("config_overwrite", JSON.stringify(configOverwrite));
      formData.append("pim_webhook_url", pimWebhookUrl);
      formData.append("image_filter_field", imageFilterField);
      formData.append("image_filter_value", imageFilterValue);
      if (isExaWebSearch) {
        formData.append(
          "exa_search_config",
          exaSearchConfigText.trim() || "{}"
        );
        formData.append("exa_max_turns", String(exaMaxTurns));
      }

      const response = await fetch(formAction, {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
        redirect: "manual",
      });

      // With redirect: "manual", successful redirects have type "opaqueredirect"
      if (response.ok || response.type === "opaqueredirect") {
        // Reload page to show success message
        window.location.reload();
      } else {
        console.error("Failed to save enrich prompt");
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error saving enrich prompt:", error);
      setIsSubmitting(false);
    }
  };

  // Update hasCustomInstructionState when instruction is manually edited
  const handleInstructionChange = (value: string): void => {
    setInstruction(value);
    // Mark as custom if user edits it
    if (!hasCustomInstructionState) {
      setHasCustomInstructionState(true);
    }
  };

  return (
    <div className="tw-w-full tw-max-w-4xl tw-mx-auto tw-space-y-6">
      <h1 className="tw-text-2xl tw-font-semibold tw-text-gray-900">
        Enrich Settings
      </h1>

      <form ref={formRef} onSubmit={handleSubmit} className="tw-space-y-6">
        {/* Prompt Settings */}
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-p-6 tw-space-y-6">
          <div className="tw-space-y-2">
            <p className="tw-text-lg tw-font-semibold tw-text-gray-600">
              Prompt Settings
            </p>
            <div className="txu-divider"></div>
            {/* Instruction Field */}
            <div className="tw-flex tw-justify-between">
              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                  Instruction
                </label>

                <p className="tw-text-xs tw-text-gray-500">
                  Overwrite the global default system instruction
                </p>
              </div>
              {hasCustomInstructionState && (
                <Button
                  type="button"
                  variant="secondary-alt"
                  icon
                  onClick={handleRevertInstruction}
                  borderless
                  data-balloon="Revert to default"
                  data-balloon-pos="left"
                >
                  <Icon name="undo" />
                </Button>
              )}
            </div>

            <div className="tw-relative">
              <textarea
                value={instruction}
                onChange={(e): void => handleInstructionChange(e.target.value)}
                rows={10}
                className="tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-p-2.5"
                placeholder={globalDefaultInstruction}
              />
            </div>
          </div>
          {/* Additional Instructions Field */}
          <div className="tw-space-y-2">
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
              Additional Instructions
            </label>
            <p className="tw-text-xs tw-text-gray-500">
              Additional instructions to give the AI, useful for adding rules
              without altering the main instruction
            </p>
            <div className="tw-space-y-3">
              {additionalInstructions.map((inst) => (
                <ListRow
                  key={inst.id}
                  instruction={inst}
                  onChange={handleAdditionalInstructionChange}
                  onRemove={handleRemoveAdditionalInstruction}
                  canRemove={additionalInstructions.length > 1}
                  placeholder="Enter additional instruction..."
                />
              ))}
              <Button
                type="button"
                variant="secondary-alt"
                icon
                onClick={handleAddAdditionalInstruction}
                className="tw-flex tw-items-center tw-gap-2"
                size="sm"
                borderless
              >
                <Icon name="add" /> Add
              </Button>
            </div>
          </div>

          {/* Use Images Toggle */}
          <div className="tw-space-y-2">
            <Toggle
              checked={useImages}
              setChecked={setUseImages}
              label="Use Images"
            />
            <p className="tw-text-xs tw-text-gray-500">
              Whether images on the document should be used in the enrichment
            </p>
          </div>
          {useImages && (
            <div className="tw-space-y-2">
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                Image filter
              </label>
              <p className="tw-text-xs tw-text-gray-500">
                Only include images whose PIM field matches this value, e.g.
                field <code>ImageType</code> value <code>Front</code>. Leave
                empty to include all images.
              </p>
              <div className="tw-flex tw-gap-2">
                <input
                  type="text"
                  value={imageFilterField}
                  onChange={(e): void => setImageFilterField(e.target.value)}
                  className="tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-px-2.5 tw-py-1.5"
                  placeholder="Field id, e.g. ResourceType"
                />
                <input
                  type="text"
                  value={imageFilterValue}
                  onChange={(e): void => setImageFilterValue(e.target.value)}
                  className="tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-px-2.5 tw-py-1.5"
                  placeholder="Required value, e.g. Front"
                />
              </div>
            </div>
          )}
          {useImages && (
            <div className="tw-space-y-2">
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                Image detail
              </label>
              <Dropdown
                control="search"
                options={imageDetailOptions}
                value={imageDetail}
                onChange={(value): void =>
                  setImageDetail((value as string) || "low")
                }
                clearable={false}
                disabled={!isOpenAiModelSelected}
              />
              <p className="tw-text-xs tw-text-gray-500">
                Detail level sent with each image. Low costs fewer input tokens,
                high lets the model read fine print on the image.
              </p>
              {!isOpenAiModelSelected && (
                <p className="tw-text-xs tw-text-gray-500">
                  Only OpenAI models support this setting. Other providers
                  ignore it, so images are sent at their own default detail
                  level regardless of this value.
                </p>
              )}
            </div>
          )}
          {isExaWebSearch && (
            <div className="tw-space-y-4">
              <p className="tw-text-base tw-font-semibold tw-text-gray-700">
                Web search settings (Exa)
              </p>
              <div className="tw-space-y-2">
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                  Exa search settings (JSON)
                </label>
                <textarea
                  value={exaSearchConfigText}
                  onChange={(e): void => setExaSearchConfigText(e.target.value)}
                  rows={10}
                  spellCheck={false}
                  className="tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-p-2.5 tw-font-mono"
                  placeholder={
                    exaConfigDefaultPlaceholder ||
                    '{\n  "type": "auto",\n  "numResults": 5\n}'
                  }
                />
                {exaConfigError && (
                  <p className="tw-text-xs tw-text-red-600">{exaConfigError}</p>
                )}
                <p className="tw-text-xs tw-text-gray-500">
                  Raw Exa /search request settings, merged over the global
                  default. Leave empty to use the global default.
                </p>
              </div>
              <div className="tw-space-y-2">
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                  Max turns
                </label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={exaMaxTurns}
                  onChange={(e): void => {
                    const parsed = parseInt(e.target.value, 10);
                    const next = Number.isFinite(parsed)
                      ? Math.max(1, Math.min(10, parsed))
                      : 1;
                    setExaMaxTurns(next);
                  }}
                  className="tw-w-32 tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-px-2.5 tw-py-1.5"
                />
                <p className="tw-text-xs tw-text-gray-500">
                  Maximum number of web search turns the model may take before
                  it must answer (1-10). If a search is unsatisfactory it can
                  search again with a new query.
                </p>
              </div>
            </div>
          )}
          {!isExaWebSearch && (
            <div className="tw-space-y-4">
              <p className="tw-text-base tw-font-semibold tw-text-gray-700">
                Web search settings
              </p>

              <div className="tw-space-y-2">
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                  Engine
                </label>
                <Dropdown
                  control="search"
                  options={engineOptions}
                  value={webSearchEngine}
                  onChange={(value): void =>
                    setWebSearchEngine((value as string) ?? "")
                  }
                  clearable
                />
                <p className="tw-text-xs tw-text-gray-500">
                  Search engine used for enrich web search.
                </p>
              </div>

              <div className="tw-space-y-2">
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                  Search prompt
                </label>
                <input
                  type="text"
                  value={webSearchPrompt}
                  onChange={(e): void => setWebSearchPrompt(e.target.value)}
                  className="tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-px-2.5 tw-py-1.5"
                  maxLength={2000}
                />
                <p className="tw-text-xs tw-text-gray-500">
                  Label injected before web search results.
                </p>
              </div>

              <div className="tw-space-y-2">
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                  Max results
                </label>
                <input
                  type="number"
                  min={1}
                  max={webSearchMaxResultsCeiling}
                  value={webSearchMaxResults}
                  onChange={(e): void => {
                    const parsed = parseInt(e.target.value, 10);
                    const next = Number.isFinite(parsed)
                      ? Math.max(
                          1,
                          Math.min(webSearchMaxResultsCeiling, parsed)
                        )
                      : 1;
                    setWebSearchMaxResults(next);
                  }}
                  className="tw-w-32 tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-px-2.5 tw-py-1.5"
                />
                <p className="tw-text-xs tw-text-gray-500">
                  Max number of web search results (1-
                  {webSearchMaxResultsCeiling}).
                </p>
              </div>

              {isExaEngine && (
                <div className="tw-space-y-2">
                  <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                    Context size
                  </label>
                  <Dropdown
                    control="search"
                    options={contextSizeOptions}
                    value={webSearchContextSize}
                    onChange={(value): void =>
                      setWebSearchContextSize((value as string) ?? "")
                    }
                    clearable
                  />
                  <p className="tw-text-xs tw-text-gray-500">
                    Controls characters per result.
                  </p>
                </div>
              )}

              <div className="tw-space-y-2">
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                  Domain filtering
                </label>
                <Dropdown
                  control="search"
                  options={domainModeOptions}
                  value={webSearchDomainMode}
                  onChange={(value): void =>
                    setWebSearchDomainMode((value as string) || "exclude")
                  }
                  clearable={false}
                  disabled={isOpenAiModelSelected}
                />
                <p className="tw-text-xs tw-text-gray-500">
                  Wildcards are supported (*.example.com). Some native providers
                  do not support all filtering options. For example, OpenAI
                  native web search does not support exclude domains.
                </p>
                {isOpenAiModelSelected && (
                  <p className="tw-text-xs tw-text-gray-500">
                    OpenAI model selected: domain filtering is locked to
                    include.
                  </p>
                )}
                <div className="tw-space-y-3">
                  {webSearchDomains.map((row) => (
                    <ListRow
                      key={row.id}
                      instruction={row}
                      onChange={handleWebSearchDomainChange}
                      onRemove={handleRemoveWebSearchDomain}
                      canRemove={webSearchDomains.length > 1}
                      inputType="input"
                      placeholder="e.g. reddit.com"
                    />
                  ))}
                  <Button
                    type="button"
                    variant="secondary-alt"
                    icon
                    onClick={handleAddWebSearchDomain}
                    className="tw-flex tw-items-center tw-gap-2"
                    size="sm"
                    borderless
                  >
                    <Icon name="add" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Model config */}
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-p-6 tw-space-y-6">
          <div className="tw-space-y-2">
            <p className="tw-text-lg tw-font-semibold tw-text-gray-600">
              Model config
            </p>
            <div className="txu-divider"></div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
              Model
            </label>
            <p className="tw-text-xs tw-text-gray-500">
              Overwrite the default GPTModel which will be used for enrichment
            </p>
            <Dropdown
              control="search"
              options={modelDropdownOptions}
              value={selectedModel?.model_name}
              onChange={(value): void =>
                setSelectedModel(
                  modelOptions.find(
                    (model: GptModel) => model.model_name == (value as string)
                  )
                )
              }
              placeholder={globalDefaultModelDisplayName}
              clearable={true}
            />
          </div>

          <ManageEnrichPromptModelSettings
            selectedModel={selectedModel}
            selectedModelName={selectedModel?.model_name || ""}
            configOverwrite={configOverwrite}
            collectConfig={collectConfig}
            modelsConfig={selectedModel?.config}
          />
        </div>

        {/* PIM Settings */}
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-p-6 tw-space-y-6">
          <div className="tw-space-y-2">
            <p className="tw-text-lg tw-font-semibold tw-text-gray-600">
              PIM Settings
            </p>
            <div className="txu-divider"></div>
            {/* Web Identification Fields */}
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
              Web Identification Fields
            </label>
            <p className="tw-text-xs tw-text-gray-500">
              Field IDs of PIM fields which help to find information about the
              product online
            </p>
            <div className="tw-space-y-3">
              {webIdentificationFields.map((field) => (
                <ListRow
                  key={field.id}
                  instruction={field}
                  onChange={handleWebIdentificationFieldChange}
                  onRemove={handleRemoveWebIdentificationField}
                  canRemove={webIdentificationFields.length > 1}
                  inputType="input"
                  placeholder="Enter web identification field id"
                />
              ))}
              <Button
                type="button"
                variant="secondary-alt"
                icon
                onClick={handleAddWebIdentificationField}
                className="tw-flex tw-items-center tw-gap-2"
                size="sm"
                borderless
              >
                <Icon name="add" />
              </Button>
            </div>
          </div>

          {/* Supplier Text Field ID */}
          <div className="tw-space-y-2">
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
              Supplier Text Field ID
            </label>
            <p className="tw-text-xs tw-text-gray-500">
              The ID of the supplier text field in PIM, this will be included in
              the input data in enrichments
            </p>
            <input
              type="text"
              value={supplierTextFieldId}
              onChange={(e): void => setSupplierTextFieldId(e.target.value)}
              className="tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-px-2.5 tw-py-1.5"
              placeholder="Enter supplier text field ID..."
            />
          </div>

          {/* PIM Datestamp Field ID */}
          <div className="tw-space-y-2">
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
              PIM Datestamp Field ID
            </label>
            <p className="tw-text-xs tw-text-gray-500">
              When an enrichment result is published to PIM, also write
              datestamp to this field
            </p>
            <input
              type="text"
              value={pimDatestampFieldId}
              onChange={(e): void => setPimDatestampFieldId(e.target.value)}
              className="tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-px-2.5 tw-py-1.5"
              placeholder="Enter PIM datestamp field ID..."
            />
          </div>

          {hasInriverIntegration !== "true" && (
            <div className="tw-space-y-2">
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                PIM Webhook URL
              </label>
              <p className="tw-text-xs tw-text-gray-500">
                URL to POST enrich data in standardized JSON format when Send to
                PIM is triggered.
              </p>
              <input
                type="url"
                value={pimWebhookUrl}
                onChange={(e): void => setPimWebhookUrl(e.target.value)}
                className="tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-px-2.5 tw-py-1.5"
                placeholder="https://..."
              />
            </div>
          )}

          {/* Submit Button */}
          <div className="tw-flex tw-justify-end tw-pt-4">
            <Button
              type="submit"
              variant="primary"
              loading={isSubmitting}
              disabled={isSubmitting || (isExaWebSearch && !!exaConfigError)}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        {/* Statistics */}
        {enrichReportDownloadUrl && (
          <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-p-6 tw-space-y-6">
            <div className="tw-space-y-2">
              <p className="tw-text-lg tw-font-semibold tw-text-gray-600">
                Statistics
              </p>
              <div className="txu-divider"></div>
              <p className="tw-text-sm tw-text-gray-600">
                Download a report comparing AI-generated Enrich results with
                user-edited values to measure where users change AI output.
              </p>
              <a
                href={enrichReportDownloadUrl}
                download
                className="tw-inline-flex tw-items-center tw-gap-2"
                data-testid="manage-enrich-prompt-download-report"
              >
                <Button type="button" variant="secondary-alt" size="sm">
                  <span className="tw-inline-flex tw-items-center tw-gap-2">
                    <Icon name="download" />
                    Download statistics report
                  </span>
                </Button>
              </a>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};
